import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Search, Building2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * INSEE Company Search Autocomplete
 * Props:
 *   onSelect(company) — called when user picks a result
 *   placeholder — input placeholder
 *   useAuth — if true, sends Authorization header (for authenticated pages)
 *   className — wrapper className
 */
export default function InseeSearch({ onSelect, placeholder = "Rechercher une entreprise (nom ou SIREN)...", useAuth: withAuth = true, className = "" }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return; }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const headers = {};
        if (withAuth) {
          const token = localStorage.getItem('token');
          if (token) headers.Authorization = `Bearer ${token}`;
        }
        const res = await axios.get(`${API_URL}/sirene/search`, { params: { q: query, limit: 8 }, headers });
        setResults(res.data.results || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [query, withAuth]);

  const handleSelect = (company) => {
    setOpen(false);
    setQuery(company.nom_complet || company.nom || '');
    if (onSelect) onSelect(company);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(r)}
              className="w-full text-left px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border-b border-slate-50 dark:border-slate-700 last:border-b-0"
            >
              <div className="flex items-start gap-2.5">
                <Building2 className="w-4 h-4 text-[#0F5156] mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{r.nom_complet || r.nom}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-slate-500 font-mono">{r.siret || r.siren}</span>
                    {r.code_postal && <span className="text-xs text-slate-400">{r.code_postal} {r.ville}</span>}
                  </div>
                  {r.libelle_naf && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{r.libelle_naf}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
